import { getCreateProjectHref } from 'base/blocks_routes';
import { Button } from 'base/ui/button';
import { formatError } from 'base/ui/form';
import { Inline, Rows } from 'base/ui/layout';
import { TickRow } from 'base/ui/tick_row';
import { TextRegularLight, TextSmallLight, Title5 } from 'base/ui/typography';
import {
  authRouteToUrl,
  getCurrentLocationForRedirect,
} from 'blocks/auth/routes';
import { SearchProjectsPageResponse } from 'blocks/blocks_service';
import React from 'react';
import styles from './states.css';
import upsellGraphic from './upsell-graphic.file.svg';

function State({
  title,
  description,
  button,
}: {
  title: React.ReactNode;
  description: React.ReactNode;
  button: React.ReactNode;
}) {
  return (
    <div className={styles.state}>
      <Rows spacing={24} xalign="center">
        <Rows spacing={8} xalign="center">
          <Title5 align="center">{title}</Title5>
          <TextRegularLight align="center">{description}</TextRegularLight>
        </Rows>
        {button && <Inline spacing={16}>{button}</Inline>}
      </Rows>
    </div>
  );
}

export function ErrorState({ error }: { error: unknown }) {
  return (
    <State
      title={formatError(error)}
      description="Please reload the page to try again."
      button={
        <Button
          size="large"
          label="Reload"
          onClick={reloadPage}
          variant="primary"
        />
      }
    />
  );
}

export function LoggedOutProjectsListState() {
  return (
    <State
      title="You are not logged in"
      description="Log in to see projects you've created."
      button={
        <>
          <Button
            size="large"
            variant="primary"
            label="Build a site"
            href={getCreateProjectHref()}
          />
          <Button
            size="large"
            label="Log in"
            href={authRouteToUrl({
              path: 'login',
              context: { redirect: getCurrentLocationForRedirect() },
            })}
          />
        </>
      }
    />
  );
}

export function TeamEmptyState({
  teamProjectsLabel,
}: {
  teamProjectsLabel: string;
}) {
  return (
    <State
      title="Your team has no shared projects"
      description={`Move a project to ${teamProjectsLabel} to share it with your team`}
      button={null}
    />
  );
}

export function getTeamProjectsLabel(
  res: SearchProjectsPageResponse | undefined,
) {
  if (res && !res.flags.teamFeaturesRequireUpgrade && res.teamName) {
    return `${res.teamName} workspace`;
  } else {
    return 'Team workspace';
  }
}

function reloadPage() {
  window.location.reload();
}

export function TeamUpsellCard() {
  return (
    <div className={styles.upsellCard}>
      <div className={styles.upsellCardText}>
        <Rows spacing={24} xalign="start">
          <Rows spacing={8} xalign="start">
            <Title5>Supercharge your design process with your team</Title5>
            <div className={styles.upsellCardSubtitle}>
              <TextSmallLight>
                Invite team members to collaborate on Site Builder projects and
                streamline your design process.
              </TextSmallLight>
            </div>
          </Rows>
          <Rows spacing={8} xalign="start">
            <TickRow>Real-time collaboration</TickRow>
            <TickRow>Shared team projects</TickRow>
            <TickRow>Centralized billing</TickRow>
          </Rows>
          <Button
            size="large"
            variant="primary"
            label="Upgrade to Pro"
            href={authRouteToUrl({
              path: 'upgrade',
              context: { redirect: getCurrentLocationForRedirect() },
            })}
          />
        </Rows>
      </div>
      <img
        className={styles.upsellCardImage}
        src={upsellGraphic}
        draggable={false}
        alt="Illustration of collaboration features in Relume Site Builder"
      />
    </div>
  );
}
